import {
  providerImportComparison,
  type ImportComparisonRow,
} from "@/features/credentials/lib/provider-import-comparison";
import type { CredentialType } from "@api/shared-runtime/enums";
import type { ProviderOfferSelection } from "./provider-import-types";

const MAX_VISIBLE_FIELD_CHANGES = 3;

export function ProviderOfferSummary({
  offer,
  stale,
}: {
  offer: ProviderOfferSelection;
  stale: boolean;
}) {
  if (stale) {
    return <p className="text-xs text-amber-800">Changed — review again</p>;
  }

  let description: string | null = null;
  if (offer.targetRemoved) {
    description = "Restore from provider";
  } else if (!offer.hasTargetHistory) {
    description = "New credential available";
  } else if (!offer.isActionable) {
    description = "Up to date";
  }

  // Match the exact field boundaries and choices used by review and import,
  // including selected values on new or restored records.
  let allChanges: ImportComparisonRow[] = [];
  if (offer.isActionable && offer.structuredChanged) {
    allChanges = providerImportComparison({
      credentialType: offer.credentialType as CredentialType,
      sourceVersion: offer.sourceVersion,
      targetVersion: offer.targetVersion,
    }).filter((row) => row.changed);
  }
  const changes = allChanges.filter(
    (row) =>
      offer.selectedFieldPaths === undefined ||
      offer.selectedFieldPaths.includes(row.path),
  );
  const hasPartialSelection = changes.length < allChanges.length;
  const remainingChanges = changes.length - MAX_VISIBLE_FIELD_CHANGES;
  if (
    !description &&
    changes.length === 0 &&
    offer.newDocumentCount === 0 &&
    offer.selectedFieldPaths === undefined
  ) {
    description = "View provider version";
  }

  return (
    <div className="space-y-1 text-xs text-slate-600">
      {description && <p>{description}</p>}
      {hasPartialSelection && (
        <p className="font-medium text-slate-900">
          {changes.length} of {allChanges.length} fields selected
        </p>
      )}
      {changes.slice(0, MAX_VISIBLE_FIELD_CHANGES).map((row) => {
        const after = row.cleared ? "Will be cleared" : row.after;
        const summary = `${row.label}: ${row.before} → ${after}`;
        return (
          <p
            key={row.path}
            className="line-clamp-2 break-words"
            title={summary}
          >
            {summary}
          </p>
        );
      })}
      {remainingChanges > 0 && (
        <p>
          +{remainingChanges} more{" "}
          {remainingChanges === 1 ? "change" : "changes"}
        </p>
      )}
      {offer.isActionable && offer.newDocumentCount > 0 && (
        <p>
          {offer.newDocumentCount} new{" "}
          {offer.newDocumentCount === 1 ? "document" : "documents"} available
        </p>
      )}
    </div>
  );
}
